import { SEARCH_TYPE_TO_LABEL, SearchType } from "@/constants";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import DatePicker from "@components/ui/date-picker";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Switch } from "@components/ui/switch";
import { Textarea } from "@components/ui/textarea";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { endOfDay, isBefore, startOfDay } from "date-fns";
import { PlusIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { QueryBuilderForm } from "../types";

type GeneralProps = {
  control: Control<QueryBuilderForm>;
  setValue: UseFormSetValue<QueryBuilderForm>;
  setError: UseFormSetError<QueryBuilderForm>;
  clearErrors: UseFormClearErrors<QueryBuilderForm>;
  startDate: Date;
  endDate?: Date;
  industries: string[];
  informationNeedError?: string;
  savedInformationNeed?: string | null;
};

export default function General({
  control,
  setValue,
  startDate,
  setError,
  clearErrors,
  endDate,
  industries,
  informationNeedError,
  savedInformationNeed,
}: GeneralProps) {
  const { t } = useLingui();

  // Information need
  const MIN_WORDS = 5;
  const informationNeed = useWatch({ control, name: "informationNeed" });
  const [showInformationNeed, setShowInformationNeed] = useState(
    Boolean(informationNeed)
  );
  // preserve typed value across toggle off/on without leaking it into submit payload while hidden
  const stashedInformationNeed = useRef<string>("");
  const [localInformationNeedError, setLocalInformationNeedError] =
    useState<string>();

  // resync stash to server-acknowledged value after successful save
  useEffect(() => {
    if (savedInformationNeed === undefined) return;
    stashedInformationNeed.current = savedInformationNeed ?? "";
  }, [savedInformationNeed]);

  function handleInformationNeedBlur() {
    if (!informationNeed) {
      setLocalInformationNeedError(undefined);
      return;
    }

    if (informationNeed.trim().split(/\s+/).length < MIN_WORDS) {
      setLocalInformationNeedError(
        t`Frågan måste vara minst ${MIN_WORDS} ord lång`
      );
      return;
    }

    setLocalInformationNeedError(undefined);
  }

  const informationNeedDisplayError =
    localInformationNeedError ?? informationNeedError;

  function handleAddEndDate() {
    const today = new Date();
    const base =
      startDate && isBefore(today, startOfDay(startDate)) ? startDate : today;
    setValue("endDate", endOfDay(base));
  }

  function handleChangeStartDate(date?: Date) {
    if (!date) {
      return;
    }

    setValue("startDate", startOfDay(date));
    clearErrors("startDate");
  }

  function handleChangeEndDate(date?: Date) {
    if (date) {
      setValue("endDate", endOfDay(date));
    } else {
      setValue("endDate", undefined);
    }

    clearErrors("endDate");
  }

  function handleDisableEndDate(date: Date) {
    if (startDate) {
      return isBefore(date, startOfDay(startDate));
    }

    return false;
  }

  function handleChangeType(type: SearchType) {
    setValue("type", type);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Basinformation</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-title";

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Titel</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      placeholder={t`Ge din bevakning en tydlig och beskrivande titel.`}
                      id={id}
                      aria-invalid={fieldState.invalid}
                      autoComplete="off"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="type"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-type";

                return (
                  <Field>
                    <FieldLabel htmlFor={id}>
                      <Trans>Söktyp</Trans>
                    </FieldLabel>
                    <Select
                      value={field.value}
                      onValueChange={(newType) => {
                        handleChangeType(newType as SearchType);
                      }}
                    >
                      <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                        <SelectValue placeholder={t`Välj vad du bevakar`} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SEARCH_TYPE_TO_LABEL).map(
                          ([value, label]) => (
                            <SelectItem
                              id={`form-qb-type-${value}`}
                              key={value}
                              value={value}
                            >
                              {i18n._(label)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => {
                const id = "form-qb-start-date";

                return (
                  <Field>
                    <FieldLabel htmlFor={id}>
                      <Trans>Startdatum</Trans>
                    </FieldLabel>
                    <DatePicker
                      value={field.value}
                      id={id}
                      aria-invalid={fieldState.invalid}
                      onChangeDate={handleChangeStartDate}
                      onInvalidDate={(_value) => {
                        setError("startDate", {
                          type: "manual",
                          message: t`${_value} är inte ett giltigt datum.`,
                        });
                      }}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            {endDate ? (
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState }) => {
                  const id = "form-qb-end-date";

                  return (
                    <Field>
                      <FieldLabel htmlFor={id}>
                        <Trans>Slutdatum</Trans>
                      </FieldLabel>
                      <DatePicker
                        id={id}
                        aria-invalid={fieldState.invalid}
                        isClearable
                        disabled={handleDisableEndDate}
                        clearLabel={t`Ta bort slutdatum`}
                        value={field.value}
                        onChangeDate={handleChangeEndDate}
                        onInvalidDate={(_value) => {
                          setError("endDate", {
                            type: "manual",
                            message: t`${_value} är inte ett giltigt datum.`,
                          });
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            ) : (
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="link"
                  className="-mx-3"
                  onClick={handleAddEndDate}
                >
                  <PlusIcon />
                  <Trans>Lägg till slutdatum</Trans>
                </Button>
              </div>
            )}

            <AccessControl level={ACCESS_LEVELS.STAFF}>
              <Controller
                name="industry"
                control={control}
                render={({ field, fieldState }) => {
                  const id = "form-qb-industry";

                  return (
                    <Field>
                      <FieldLabel staff htmlFor={id}>
                        <Trans>Branschindex (använd ej)</Trans>
                      </FieldLabel>
                      <Select
                        value={field.value ?? ""}
                        onValueChange={(value) => {
                          if (value === "none") {
                            field.onChange(null);
                          } else {
                            field.onChange(value);
                          }
                        }}
                      >
                        <SelectTrigger
                          id={id}
                          aria-invalid={fieldState.invalid}
                        >
                          <SelectValue placeholder={t`Inget`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <Trans>Inget</Trans>
                          </SelectItem>
                          {industries.map((industry) => (
                            <SelectItem key={industry} value={industry}>
                              {industry}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            </AccessControl>
          </div>
        </FieldGroup>

        <AccessControl level={ACCESS_LEVELS.STAFF}>
          <div className="flex flex-col gap-3 w-full max-w-2xl mt-5">
            <Field orientation="horizontal">
              <FieldContent>
                <FieldLabel staff>
                  <Trans>Smart relevans (BETA)</Trans>
                </FieldLabel>
                <FieldDescription>
                  <Trans>
                    Förfina vilka omnämnanden som anses relevanta genom att
                    lägga till en ja/nej-fråga.
                  </Trans>
                </FieldDescription>
              </FieldContent>
              <Switch
                checked={showInformationNeed}
                onCheckedChange={(checked: boolean) => {
                  setShowInformationNeed(checked);
                  if (!checked) {
                    stashedInformationNeed.current = informationNeed ?? "";
                    setValue("informationNeed", "");
                    setLocalInformationNeedError(undefined);
                    return;
                  }
                  if (stashedInformationNeed.current) {
                    setValue("informationNeed", stashedInformationNeed.current);
                  }
                }}
              />
            </Field>

            {showInformationNeed && (
              <Controller
                name="informationNeed"
                control={control}
                render={({ field }) => (
                  <Field data-invalid={Boolean(informationNeedDisplayError)}>
                    <Textarea
                      aria-invalid={Boolean(informationNeedDisplayError)}
                      placeholder={t`T.ex. "Nämner den här texten Asker, sjukvårdsföretaget?"`}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e.currentTarget.value);
                        if (localInformationNeedError)
                          setLocalInformationNeedError(undefined);
                      }}
                      onBlur={handleInformationNeedBlur}
                    />
                    {informationNeedDisplayError && (
                      <FieldError>{informationNeedDisplayError}</FieldError>
                    )}
                  </Field>
                )}
              />
            )}
          </div>
        </AccessControl>
      </CardContent>
    </Card>
  );
}
